import { useState } from "react";
import { genres, regions, years } from "@/mocks/filters";

export interface FilterState {
  genre: string;
  region: string;
  year: string;
}

interface FilterPanelProps {
  value: FilterState;
  onChange: (next: Partial<FilterState>) => void;
}

interface ChipRowProps {
  label: string;
  options: { value: string; label: string }[];
  active: string;
  onPick: (value: string) => void;
}

function ChipRow({ label, options, active, onPick }: ChipRowProps) {
  return (
    <div className="flex flex-col gap-2 border-b border-background-200 py-4 last:border-b-0 md:flex-row md:items-start md:gap-4">
      <span className="w-16 shrink-0 text-sm font-medium text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onPick(option.value)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              active === option.value
                ? "bg-primary-600 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-900"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterPanel({ value, onChange }: FilterPanelProps) {
  const [open, setOpen] = useState(false);

  const genreOptions = [{ value: "all", label: "全部" }].concat(
    genres.map((g) => ({ value: g, label: g })),
  );
  const regionOptions = [{ value: "all", label: "全部" }].concat(
    regions.map((r) => ({ value: r, label: r })),
  );
  const yearOptions = [{ value: "all", label: "全部" }].concat(
    years.map((y) => ({ value: String(y), label: `${y}` })),
  );

  const activeCount = [value.genre, value.region, value.year].filter((v) => v !== "all").length;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between px-4 py-3 md:hidden"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-foreground-800">
          <i className="ri-filter-3-line text-lg"></i>
          筛选条件
          {activeCount > 0 && (
            <span className="rounded-full bg-accent-500 px-2 py-0.5 text-[11px] font-bold text-background-50">
              {activeCount}
            </span>
          )}
        </span>
        <i className={`${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-xl text-foreground-500`}></i>
      </button>

      <div className={`${open ? "block" : "hidden"} px-4 pb-2 md:block md:px-6`}>
        <ChipRow
          label="类型"
          options={genreOptions}
          active={value.genre}
          onPick={(v) => onChange({ genre: v })}
        />
        <ChipRow
          label="地区"
          options={regionOptions}
          active={value.region}
          onPick={(v) => onChange({ region: v })}
        />
        <ChipRow
          label="年份"
          options={yearOptions}
          active={value.year}
          onPick={(v) => onChange({ year: v })}
        />
      </div>
    </div>
  );
}